/**
 * useIntersectionObserver Hook - Phase 4e: Lazy Loading Refinement
 *
 * Triggers asset preloading at strategic scroll positions:
 * - Prefetch next 2 questions when user is 80% through current section
 * - Load full animation video 2 questions ahead (non-blocking)
 * - Only activate on fast connections (avoid bandwidth waste on 2G)
 */

import { useEffect, useRef, useCallback } from 'react';
import { logger } from '../utils/logger';
import { preloadAnimationsForQuestions } from '../components/PracticeTest/QuestionAnimationPlayer';
import { networkAwareLoading } from '../services/networkAwareLoading';

interface UseIntersectionObserverOptions {
  threshold?: number | number[]; // When to trigger (0-1, default 0.8 = 80% visible)
  rootMargin?: string; // Margin around viewport (default '0px')
  prefetchDistance?: number; // How many questions ahead to prefetch (default 2)
  enabled?: boolean; // Whether to enable prefetching (default true)
}

/**
 * Intersection Observer hook for smart prefetching
 *
 * Usage:
 * const ref = useIntersectionObserver(
 *   questions,
 *   currentIndex,
 *   {
 *     threshold: 0.8,
 *     prefetchDistance: 2
 *   }
 * );
 */
export function useIntersectionObserver(
  questions: Array<any> = [],
  currentIndex: number = 0,
  options: UseIntersectionObserverOptions = {}
): React.RefObject<HTMLDivElement> {
  const {
    threshold = 0.8,
    rootMargin = '0px',
    prefetchDistance = 2,
    enabled = true
  } = options;

  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const prefetchNextQuestions = useCallback(() => {
    if (!enabled || !questions || questions.length === 0) return;

    // Only prefetch on fast or medium connections
    const qualityTier = networkAwareLoading.getQualityTier();
    if (qualityTier.speed === 'slow' || qualityTier.speed === 'offline') {
      logger.debug('Skipping prefetch on slow connection');
      return;
    }

    // Calculate which questions to prefetch
    const startIndex = Math.min(currentIndex + 1, questions.length);
    const endIndex = Math.min(startIndex + prefetchDistance, questions.length);

    if (startIndex >= questions.length) {
      logger.debug('No more questions to prefetch');
      return;
    }

    const questionsToPreload = questions.slice(startIndex, endIndex);

    if (questionsToPreload.length === 0) return;

    logger.debug(`Intersection prefetch: questions ${startIndex}-${endIndex - 1}`, {
      currentIndex,
      connectionSpeed: qualityTier.speed
    });

    // Trigger preload using requestIdleCallback
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        preloadAnimationsForQuestions(questionsToPreload);
      });
    } else {
      setTimeout(() => {
        preloadAnimationsForQuestions(questionsToPreload);
      }, 100);
    }
  }, [currentIndex, questions, prefetchDistance, enabled]);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    // Create Intersection Observer
    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          logger.debug('Question section is 80% visible - triggering prefetch');
          prefetchNextQuestions();
        }
      });
    };

    observerRef.current = new IntersectionObserver(callback, {
      threshold,
      rootMargin
    });

    observerRef.current.observe(ref.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, prefetchNextQuestions, enabled]);

  return ref;
}

/**
 * Alternative: useScrollPrefetch
 * Prefetch based on scroll position (more aggressive)
 */
export function useScrollPrefetch(
  questions: Array<any> = [],
  currentIndex: number = 0,
  options: {
    prefetchDistance?: number;
    enabled?: boolean;
    scrollThreshold?: number; // Scroll distance before prefetch (in pixels)
  } = {}
): void {
  const {
    prefetchDistance = 2,
    enabled = true,
    scrollThreshold: _scrollThreshold = 500
  } = options;

  const lastPrefetchRef = useRef(0);

  useEffect(() => {
    if (!enabled || !questions) return;

    const handleScroll = () => {
      const now = Date.now();

      // Debounce: only prefetch every 2 seconds max
      if (now - lastPrefetchRef.current < 2000) {
        return;
      }

      // Check if user is near bottom of current question
      const scrollPercentage =
        (window.innerHeight + window.scrollY) / document.documentElement.scrollHeight;

      // Prefetch when user is 70% through the page
      if (scrollPercentage > 0.7) {
        lastPrefetchRef.current = now;

        const startIndex = Math.min(currentIndex + 1, questions.length);
        const endIndex = Math.min(startIndex + prefetchDistance, questions.length);

        if (startIndex < questions.length) {
          const questionsToPreload = questions.slice(startIndex, endIndex);
          logger.debug(`Scroll prefetch: questions ${startIndex}-${endIndex - 1}`);

          if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => {
              preloadAnimationsForQuestions(questionsToPreload);
            });
          }
        }
      }
    };

    // Use passive listener for performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentIndex, questions, prefetchDistance, enabled]);
}

/**
 * useImageVisibility Hook
 * Trigger image loading when image element becomes visible
 */
export function useImageVisibility(
  options: {
    threshold?: number;
    rootMargin?: string;
    onVisible?: () => void;
  } = {}
) {
  const {
    threshold = 0.1,
    rootMargin = '100px',
    onVisible
  } = options;

  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            logger.debug('Image is visible - triggering load');
            onVisible?.();
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, onVisible]);

  return ref;
}
